import { useIsMutating } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'

import { REORDER_KEY } from '@/shared/hooks/use-reorder-mutation'
import { cn } from '@/shared/lib/cn'
import { useT } from '@/shared/i18n'

export interface SaveIndicatorProps {
  /** Cuánto queda visible el "Guardado" antes de desaparecer. */
  settleMs?: number
  className?: string
}

/**
 * "Guardando… / Guardado" en el header.
 *
 * **Nunca un spinner sobre las filas** (plan 3.4): un spinner encima de la fila
 * que acabás de mover tapa justo lo que querés mirar, y cambia de tamaño el
 * contenido. Esto es una línea de texto discreta, con `aria-live` para que
 * también se anuncie.
 */
export function SaveIndicator({ settleMs = 2000, className }: SaveIndicatorProps) {
  const t = useT()
  const pending = useIsMutating({ mutationKey: REORDER_KEY })
  const [justSaved, setJustSaved] = useState(false)
  const previous = useRef(pending)

  useEffect(() => {
    const wasPending = previous.current > 0
    previous.current = pending

    if (!wasPending || pending !== 0) return

    setJustSaved(true)
    const timer = setTimeout(() => setJustSaved(false), settleMs)
    return () => clearTimeout(timer)
  }, [pending, settleMs])

  const label = pending > 0 ? t('common.state.saving') : justSaved ? t('common.state.saved') : null

  return (
    <span
      aria-live="polite"
      className={cn(
        'text-2xs transition-opacity duration-[var(--motion-base)]',
        pending > 0 ? 'text-text-2' : 'text-text-3',
        label ? 'opacity-100' : 'opacity-0',
        className,
      )}
    >
      {label}
    </span>
  )
}
